import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  browserTimezone,
  TrackerHistory,
  useTracker,
  useTrackerSettings,
  WeeklyTracker,
} from "@/features/tracker";

export const Route = createFileRoute("/_authenticated/me/tracker")({
  component: TrackerPage,
});

/**
 * "10 Minutes a Week", digitised. Something to open because they want to,
 * or never open at all; neither is a problem.
 */
function TrackerPage() {
  const { data, isPending, isError, refetch } = useTracker();
  const save = useTrackerSettings();
  const enabled = data?.settings.enabled ?? false;
  const stored = data?.settings.timezone;

  // The week turns over at their own midnight, so keep the zone current: the
  // one in effect when the week turns over is the one that counts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the stored zone changes, not on every render of the mutation object
  useEffect(() => {
    const here = browserTimezone();
    if (enabled && stored && stored !== here) save.mutate({ body: { timezone: here } });
  }, [enabled, stored]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        eyebrow="My space"
        title="Weekly tracker"
        description="Pick a few things for the week and tick them off. Yours alone."
      />
      {isPending ? (
        <Skeleton className="h-64 rounded-2xl" />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : !enabled ? (
        <Card>
          <CardContent className="space-y-3">
            <h2 className="font-semibold text-foreground">Your weekly tracker is off</h2>
            <p className="text-muted-foreground text-sm">
              Turn it on to pick up to three things a week. You can turn it off again whenever you
              like, and nothing you did is lost.
            </p>
            <Button
              disabled={save.isPending}
              onClick={() => save.mutate({ body: { enabled: true, timezone: browserTimezone() } })}
            >
              Turn it on
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <WeeklyTracker state={data} />
          <TrackerHistory state={data} />
        </>
      )}
    </div>
  );
}
